import {
  Directive,
  OnInit,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  EventEmitter,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { FormInfoService } from '../services/dynamic-forms/form-info.service';
import { DynamicFormsService } from '../services/dynamic-forms/dynamic-forms.service';
import { ErrorDefinition } from '../models/error-definition';
import { IDynamicFormControl } from '../models/dynamic-form-control';
import { IDynamicFormGroupDefinition } from '../models/dynamic-form-group';
import { ErrorService } from '../services/form-utility/error.service';
import { DynamicFormLoadDirective } from '../directives/dynamic-form-structural.directive';

@Directive({
  selector: 'app-base-input',
})
export class BaseInputDirective implements OnInit, OnChanges {
  @Input() hint: string;
  @Input() tooltipText: string;
  @Input() labelOverride: string;
  @Input() requiredErrorMessage: string;
  @Input() placeholder: string;
  @Input() formInfo: IDynamicFormGroupDefinition;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('form') formGroupOverride: FormGroup;
  @Input() controlName: string;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('data-automation-id') dataAutomationId: string;
  @Input() disabled: boolean;
  @Input() readonly: boolean;
  @Input() hideLabel: boolean;
  @Input() fieldDescription: string;
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() change = new EventEmitter();
  @Input() isTime: boolean;

  fgInfo: IDynamicFormControl;
  formControl: FormControl;
  errorDefinitions: ErrorDefinition[];

  constructor(
    protected formInfoService: FormInfoService,
    protected dynamicFormsService: DynamicFormsService,
    protected errorService: ErrorService,
    protected dynamicFormDirective: DynamicFormLoadDirective,
  ) {}

  ngOnInit() {
    // Allow a user provided value, but fall back to formInfoService's value
    if (this.formGroupOverride && !this.formInfo) {
      const foundInfo = (<any>this.formGroupOverride)?.dynamicFormGroupDefinition;
      if (foundInfo && foundInfo.formGroup === this.formGroupOverride) {
        this.formInfo = foundInfo;
      }
    }
    if (this.formInfo == null && this.dynamicFormDirective) {
      this.formInfo = this.dynamicFormDirective.formInfo;
    }

    if (this.controlName) {
      const cleanControlName = this.controlName[0].toLowerCase() + this.controlName.substr(1);

      // Init variables
      this.fgInfo = this.dynamicFormsService.findControl(this.formInfo, cleanControlName);

      if (this.fgInfo == null) {
        throw new Error(`Control ${this.controlName} not found in dynamicFormsService.`);
      }

      if (this.formGroupOverride == null) {
        this.formControl = this.formInfo.formGroup.get(cleanControlName) as FormControl;
      } else {
        this.formControl = this.formGroupOverride.get(cleanControlName) as FormControl;
      }
    }

    // Collect error definitions to display as mat-errors
    this.errorDefinitions = this.getErrorDefinitions();

    // Check to see if the input is disabled
    this.evaluateDisabled(this.disabled);

    if (this.fgInfo) {
      if (this.labelOverride) {
        this.fgInfo.label = this.labelOverride;
      }

      if (!this.fgInfo.maxLength || this.fgInfo.maxLength <= 0 || this.fgInfo.mask) {
        this.fgInfo.maxLength = 1000;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.formControl != null && changes['disabled'] != null) {
      this.evaluateDisabled(changes['disabled'].currentValue);
    }
  }

  getErrorDefinitions(): ErrorDefinition[] {
    if (this.fgInfo) {
      if (this.requiredErrorMessage) {
        this.fgInfo.requiredMessage = this.requiredErrorMessage;
      }

      // Get the generic errors
      const errors = this.errorService.getGenericErrors(this.formControl, this.fgInfo);

      // Add any additional validation errors if they exist
      if (this.fgInfo.additionalErrors) {
        for (const additionalErrorKey of Object.keys(this.fgInfo.additionalErrors)) {
          const errorDef = this.errorService.createErrorDefinition(
            this.formControl,
            additionalErrorKey,
          );
          errors.push(errorDef);
        }
      }

      // Append hint or placeholder to error message
      if (this.fgInfo.mask && this.fgInfo.mask.trim !== '') {
        const hint = !this.hint ? this.placeholder : this.hint;
        const appendHintAsError = new ErrorDefinition(
          'hint',
          () => true,
          () => hint,
        );
        errors.unshift(appendHintAsError);
      }

      return errors;
    } else {
      return [];
    }
  }

  getRequired(): boolean {
    return this.fgInfo?.required || this.formControl?.['condrequired'];
  }

  protected evaluateDisabled(disabledValue: boolean) {
    if (this.formControl) {
      if (disabledValue) {
        this.formControl.disable();
      } else {
        this.formControl.enable();
      }
    }
  }
}
